import React from 'react';
import PropTypes from 'prop-types';
import { ArrowRight, Check } from 'lucide-react';
import Badge from 'react-bootstrap/lib/Badge';
import AriAvatar from 'containers/Ari/AriAvatar';
import { trackEvent } from 'helpers/Analytics';
import styles from './AriUpsell.module.scss';

const PROOF_POINTS = [
  'Real time, as jobs go up',
  'Matched to your experience',
  'Two minutes to set up',
];

const usesCvTerm = (user) => {
  const location = user?.location?.text || '';
  if (/United States|Canada/i.test(location)) {
    return false;
  }
  return true;
};

const getPortfolioNoun = (user) => (usesCvTerm(user) ? 'CV' : 'resume');

const AriUpsell = ({ user, mobile, alwaysShow, compactButtons }) => {
  const isLoggedIn = !!user;
  const showFootnote = !user || (!user.pro && !user.premium);
  const portfolioNoun = getPortfolioNoun(user);
  const onAskClick = () => trackEvent('ari-sidebar-clicked');
  const onLearnMoreClick = () => trackEvent('ari-sidebar-learn-more-clicked');
  const askHref = isLoggedIn ? '/ari' : '/freelance/ari';
  const learnMoreHref = '/freelance/ari';

  let visibility = '';
  if (!alwaysShow) {
    visibility = mobile ? styles.upsellMobile : styles.upsellDesktop;
  }

  const className = `${styles.upsellContainer} ${visibility}`.trim();

  return (
    <div className={className}>
      <div className={styles.kickerRow}>
        <AriAvatar size={32} className={styles.avatar} />
        <div className={styles.kickerText}>
          <span className={styles.kickerName}>Ari</span>
          <span className={styles.kickerRole}>Twine’s job assistant</span>
        </div>
        {isLoggedIn && (
          <Badge
            className={styles.newBadge}
            style={{ backgroundColor: '#0777DA' }}
          >
            New
          </Badge>
        )}
      </div>

      <h4 className={styles.headline}>
        You do the work. Ari does the job searching.
      </h4>

      <p className={styles.body}>
        Ari scans thousands of project-based jobs in real time, across the web,
        company careers pages and Twine, and matches them to your portfolio or{' '}
        {portfolioNoun}. You decide what to apply for.
      </p>

      <ul className={styles.proofList}>
        {PROOF_POINTS.map((point) => (
          <li key={point} className={styles.proofItem}>
            <span className={styles.proofIcon} aria-hidden="true">
              <Check size={14} strokeWidth={2.5} />
            </span>
            <span>{point}</span>
          </li>
        ))}
      </ul>

      <div
        className={`${styles.actions} ${
          compactButtons ? styles.actionsCompact : styles.actionsFull
        }`}
      >
        <a href={askHref} className={styles.askButton} onClick={onAskClick}>
          {isLoggedIn ? 'Ask Ari to search' : 'Meet Ari'}
          <ArrowRight size={16} aria-hidden="true" />
        </a>
        <a
          href={learnMoreHref}
          className={styles.learnMoreLink}
          onClick={onLearnMoreClick}
        >
          See how Ari works
        </a>
      </div>

      {showFootnote && (
        <p className={styles.footnote}>
          Free members get three Ari searches a week. Pro is unlimited.
        </p>
      )}
    </div>
  );
};

AriUpsell.propTypes = {
  user: PropTypes.object,
  mobile: PropTypes.bool,
  alwaysShow: PropTypes.bool,
  compactButtons: PropTypes.bool,
};

AriUpsell.defaultProps = {
  mobile: false,
  alwaysShow: false,
  compactButtons: false,
};

export default AriUpsell;
